<script setup lang="ts">
/**
 * Incorporation DOC (M32014) — Section 14 / Section 201 (± attestation).
 * Frontend Studio live document (DirectorResignationDoc pattern).
 * Section 201: 내국인 → Declaration only / 외국인 → Attestation only (디렉터당 1부).
 */
import { ref, computed, inject, watch, nextTick } from 'vue';
import CddDocVarField from '../cdd/CddDocVarField.vue';
import PagedDoc from '../resignation/PagedDoc.vue';
import { CDD_DOC_CTX } from '../cdd/cddContext';
import { isForeignDirector } from '../../mappers/incorporationDocMapper';

// hideSection201: M32100 스튜디오처럼 Section 201 을 별도 Stage 로 뺀 경우 여기선 Section 14 만 렌더.
defineProps<{ hideSection201?: boolean }>();

const ctx = inject(CDD_DOC_CTX, null);
const section14Paged = ref<{ relayout?: () => void } | null>(null);

const directorRows = ref<number[]>([1]);
const memberRows = ref<number[]>([1]);
let dirSeq = 1;
let memSeq = 1;

function addDirector() {
  dirSeq += 1;
  directorRows.value.push(dirSeq);
}
function removeDirector(id: number) {
  if (directorRows.value.length <= 1) return;
  directorRows.value = directorRows.value.filter((r) => r !== id);
}
function addMember() {
  memSeq += 1;
  memberRows.value.push(memSeq);
}
function removeMember(id: number) {
  if (memberRows.value.length <= 1) return;
  memberRows.value = memberRows.value.filter((r) => r !== id);
}

function valuesSnapshot(): Record<string, string> {
  const out: Record<string, string> = {};
  if (!ctx) return out;
  for (const id of directorRows.value) {
    out[`dir${id}_nationality`] = ctx.valueOf(`dir${id}_nationality`) || '';
    out[`dir${id}_id_type`] = ctx.valueOf(`dir${id}_id_type`) || '';
  }
  return out;
}

/** 내국인 디렉터 → Section 201 Declaration */
const localDirectorRows = computed(() => {
  const snap = valuesSnapshot();
  return directorRows.value.filter((id) => !isForeignDirector(snap, id));
});

/** 외국인 디렉터 → Section 201 with Attestation */
const foreignDirectorRows = computed(() => {
  const snap = valuesSnapshot();
  return directorRows.value.filter((id) => isForeignDirector(snap, id));
});

const multipleDirs = computed(() => directorRows.value.length > 1);
/** Section 14(1) + 디렉터당 Section 201(1) */
const docCount = computed(() => 1 + directorRows.value.length);

function gatherLocalState() {
  return {
    directorRows: [...directorRows.value],
    memberRows: [...memberRows.value],
    dirSeq,
    memSeq,
  };
}
function restoreLocalState(s: any) {
  if (!s) return;
  if (Array.isArray(s.directorRows) && s.directorRows.length) {
    directorRows.value = [...s.directorRows];
    dirSeq = Math.max(dirSeq, ...s.directorRows);
  }
  if (Array.isArray(s.memberRows) && s.memberRows.length) {
    memberRows.value = [...s.memberRows];
    memSeq = Math.max(memSeq, ...s.memberRows);
  }
  if (typeof s.dirSeq === 'number') dirSeq = s.dirSeq;
  if (typeof s.memSeq === 'number') memSeq = s.memSeq;
  nextTick(() => section14Paged.value?.relayout?.());
}

function relayoutPages() {
  nextTick(() => section14Paged.value?.relayout?.());
}

watch([directorRows, memberRows], () => {
  relayoutPages();
}, { deep: true });

defineExpose({ gatherLocalState, restoreLocalState, relayoutPages });
</script>

<template>
  <div class="rsn-sheets">
    <!-- ══════════ Document 1 · Section 14 ══════════ -->
    <div class="rsn-doc">
      <div class="rsn-doclabel">
        <span class="ic">📄</span>
        <span class="nm">Section 14 — Particulars of Company</span>
        <span class="n">Document 1 / {{ docCount }}</span>
      </div>
      <PagedDoc ref="section14Paged">
        <div class="pg-unit rsn-center">
          <div class="rsn-act">COMPANIES ACT 2016</div>
          <div class="rsn-act sm">Section 14</div>
          <h2 class="rsn-h" style="text-decoration:none;margin-top:10px">PARTICULARS OF COMPANY</h2>
        </div>

        <div class="pg-unit">
          <table class="cdd-tbl">
            <tbody>
              <tr>
                <td class="cdd-lcell">Proposed company name</td>
                <td><CddDocVarField field-key="company_name" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Lodging Reference No</td>
                <td><CddDocVarField field-key="lodging_ref_no" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Purpose</td>
                <td><CddDocVarField field-key="purpose" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Company Type</td>
                <td><CddDocVarField field-key="company_type" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Sub Type</td>
                <td><CddDocVarField field-key="company_sub_type" /></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="pg-unit">
          <div class="rsn-secbar">General nature of business</div>
          <table class="cdd-tbl">
            <tbody>
              <tr class="rsn-list-h">
                <th style="width:34px">#</th>
                <th>Business Nature</th>
                <th style="width:140px">MSIC Code</th>
              </tr>
              <tr v-for="n in 3" :key="'msic-' + n">
                <td class="rsn-idx">{{ n }}</td>
                <td><CddDocVarField :field-key="`business_nature_${n}`" /></td>
                <td><CddDocVarField :field-key="`msic_code_${n}`" /></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="inc-block-gap" aria-hidden="true"></div>

        <!-- Word: Business Description ~ Business Fax 한 표 -->
        <div class="pg-unit">
          <table class="cdd-tbl">
            <tbody>
              <tr class="rsn-tall">
                <td class="cdd-lcell">Business Description</td>
                <td><CddDocVarField field-key="business_description" /></td>
              </tr>
              <tr class="rsn-tall">
                <td class="cdd-lcell">Registered Address</td>
                <td><CddDocVarField field-key="registered_address" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Email</td>
                <td><CddDocVarField field-key="registered_email" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Office No</td>
                <td><CddDocVarField field-key="registered_office_no" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Fax Number</td>
                <td><CddDocVarField field-key="registered_fax_no" /></td>
              </tr>
              <tr class="rsn-tall">
                <td class="cdd-lcell">Business Address</td>
                <td><CddDocVarField field-key="business_address" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Email</td>
                <td><CddDocVarField field-key="business_email" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Office No</td>
                <td><CddDocVarField field-key="business_office_no" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Fax Number</td>
                <td><CddDocVarField field-key="business_fax_no" /></td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Directors: Word [[if director]] 표 블록 반복 — 페이지 단위 keep -->
        <div
          v-for="(id, i) in directorRows"
          :key="'d-' + id"
          class="pg-unit inc-part"
        >
          <table class="cdd-tbl">
            <tbody>
              <tr class="rsn-part-h">
                <td colspan="2">
                  <span>PARTICULARS OF DIRECTOR</span>
                  <button
                    v-if="directorRows.length > 1"
                    type="button"
                    class="rsn-x part-x"
                    title="Remove"
                    @click="removeDirector(id)"
                  >&times;</button>
                  <span v-if="multipleDirs" class="part-n">{{ i + 1 }} / {{ directorRows.length }}</span>
                </td>
              </tr>
              <tr>
                <td class="cdd-lcell">Director Name</td>
                <td><CddDocVarField :field-key="`dir${id}_name`" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">ID Type</td>
                <td><CddDocVarField :field-key="`dir${id}_id_type`" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Identification No</td>
                <td><CddDocVarField :field-key="`dir${id}_id_no`" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Nationality</td>
                <td><CddDocVarField :field-key="`dir${id}_nationality`" /></td>
              </tr>
              <tr class="rsn-tall rsn-addr">
                <td class="cdd-lcell">Address</td>
                <td><CddDocVarField :field-key="`dir${id}_address`" /></td>
              </tr>
              <tr class="rsn-blank"><td colspan="2">&nbsp;</td></tr>
              <tr>
                <td class="cdd-lcell">Date of birth</td>
                <td><CddDocVarField :field-key="`dir${id}_dob`" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Gender</td>
                <td><CddDocVarField :field-key="`dir${id}_gender`" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Race</td>
                <td><CddDocVarField :field-key="`dir${id}_race`" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Email</td>
                <td><CddDocVarField :field-key="`dir${id}_email`" /></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="pg-unit">
          <button type="button" class="rsn-add inc-add" @click="addDirector">
            <span class="rsn-add-ic" aria-hidden="true"></span> Add director
          </button>
        </div>

        <!-- Members: Word [[if member]] 표 블록 반복 -->
        <div
          v-for="(id, i) in memberRows"
          :key="'m-' + id"
          class="pg-unit inc-part"
        >
          <table class="cdd-tbl">
            <tbody>
              <tr class="rsn-part-h">
                <td colspan="2">
                  <span>PARTICULARS OF MEMBER</span>
                  <button
                    v-if="memberRows.length > 1"
                    type="button"
                    class="rsn-x part-x"
                    title="Remove"
                    @click="removeMember(id)"
                  >&times;</button>
                  <span v-if="memberRows.length > 1" class="part-n">{{ i + 1 }} / {{ memberRows.length }}</span>
                </td>
              </tr>
              <tr>
                <td class="cdd-lcell">Member Name</td>
                <td><CddDocVarField :field-key="`mem${id}_name`" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">ID Type</td>
                <td><CddDocVarField :field-key="`mem${id}_id_type`" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Identification No</td>
                <td><CddDocVarField :field-key="`mem${id}_id_no`" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Nationality</td>
                <td><CddDocVarField :field-key="`mem${id}_nationality`" /></td>
              </tr>
              <tr class="rsn-tall rsn-addr">
                <td class="cdd-lcell">Address</td>
                <td><CddDocVarField :field-key="`mem${id}_address`" /></td>
              </tr>
              <tr class="rsn-blank"><td colspan="2">&nbsp;</td></tr>
              <tr>
                <td class="cdd-lcell">Gender</td>
                <td><CddDocVarField :field-key="`mem${id}_gender`" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Race</td>
                <td><CddDocVarField :field-key="`mem${id}_race`" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Email</td>
                <td><CddDocVarField :field-key="`mem${id}_email`" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Price per share</td>
                <td><CddDocVarField :field-key="`mem${id}_price_per_share`" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Class of share</td>
                <td><CddDocVarField :field-key="`mem${id}_class_of_share`" /></td>
              </tr>
              <tr>
                <td class="cdd-lcell">Number of share</td>
                <td><CddDocVarField :field-key="`mem${id}_no_of_shares`" /></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="pg-unit">
          <button type="button" class="rsn-add inc-add" @click="addMember">
            <span class="rsn-add-ic" aria-hidden="true"></span> Add member
          </button>
        </div>

        <div class="pg-unit">
          <p class="rsn-p" style="margin-top:14px">
            Total number of shares:
            <CddDocVarField field-key="total_no_of_shares" />
          </p>

          <div class="rsn-decl">
            <div class="rsn-sign-t">Declaration</div>
            <p class="rsn-p">
              I confirm that the facts and information stated in this document are true and to the best of my knowledge.
            </p>
            <div class="rsn-line">Name: <CddDocVarField field-key="signer_name" /></div>
            <div class="rsn-line">Passport / NRIC No.: <CddDocVarField field-key="signer_nric_passport_no" /></div>
            <div class="rsn-date">Date: <CddDocVarField field-key="declaration_date" /></div>
          </div>
        </div>
      </PagedDoc>
    </div>

    <!-- ══════════ Document 2 · Section 201 Declaration (local directors only) ══════════ -->
    <div v-for="(id, i) in (hideSection201 ? [] : localDirectorRows)" :key="'s201-' + id" class="rsn-doc">
      <div class="rsn-doclabel">
        <span class="ic">📋</span>
        <span class="nm">
          Section 201 — Declaration (Without Attestation)
          <span v-if="localDirectorRows.length > 1" class="sub"> · {{ i + 1 }} of {{ localDirectorRows.length }}</span>
        </span>
        <span class="n">Document {{ 2 + i }} / {{ docCount }}</span>
      </div>
      <PagedDoc>
        <div class="rsn-center">
          <div class="rsn-act">SECTION 201 — DECLARATION</div>
          <div class="rsn-act sm">
            Declaration by a Person Before Appointment as Director, or by a Promoter
            Before Incorporation of Corporation — Without Attestation
          </div>
        </div>

        <p class="rsn-p bold">DECLARATION</p>
        <p class="rsn-p">I solemnly and sincerely declare that:</p>
        <ol class="inc-ol">
          <li>I am not an undischarged bankrupt.</li>
          <li>
            I have not been convicted whether within or outside Malaysia of any offence:
            <ol type="a">
              <li>in connection with the promotion, formation or management of a corporation;</li>
              <li>involving fraud or dishonesty punishable on conviction with imprisonment for three months or more; or</li>
              <li>under section 213, 217, 218, 228 or 539, within a period of five years preceding the date of this declaration.</li>
            </ol>
          </li>
          <li>
            I have not been imprisoned for any offence referred to in paragraph 2 within the period of five years
            immediately preceding the date of this declaration.
          </li>
          <li class="dim">
            * I am an undischarged bankrupt but have been granted leave by the court under section 198(3)(b)
            to act as a director of …………………… (name of corporation)
          </li>
          <li class="dim">
            * I have been granted leave by Court under section 198 to be a director / promoter …
            (* Strike out whichever is inapplicable)
          </li>
          <li>
            I hereby consent to act as director of
            <CddDocVarField field-key="company_name" />.
            I confirm that the facts and information stated in this document are true.
          </li>
        </ol>

        <div class="rsn-yours">
          <div>Sign by Director(s)/Promoter(s):</div>
          <div class="rsn-sign-line">______________________________</div>
          <div>Name: <CddDocVarField :field-key="`dir${id}_name`" /></div>
          <div>NRIC / Passport No.: <CddDocVarField :field-key="`dir${id}_id_no`" /></div>
          <div>Date: <CddDocVarField field-key="declaration_date" /></div>
        </div>
      </PagedDoc>
    </div>

    <!-- ══════════ Document 3 · Section 201 with attestation (foreign directors only) ══════════ -->
    <div v-for="(id, i) in (hideSection201 ? [] : foreignDirectorRows)" :key="'s201a-' + id" class="rsn-doc">
      <div class="rsn-doclabel">
        <span class="ic">⚖️</span>
        <span class="nm">
          Section 201 — Declaration (With Attestation)
          <span v-if="foreignDirectorRows.length > 1" class="sub"> · {{ i + 1 }} of {{ foreignDirectorRows.length }}</span>
        </span>
        <span class="n">Document {{ 2 + localDirectorRows.length + i }} / {{ docCount }}</span>
      </div>
      <PagedDoc>
        <div class="rsn-center">
          <div class="rsn-act">SECTION 201 — DECLARATION</div>
          <div class="rsn-act sm">
            Declaration by a Person Before Appointment as Director, or by a Promoter
            Before Incorporation of Corporation — With Attestation by Commissioner of Oaths / Notary Public
          </div>
        </div>

        <p class="rsn-p bold">DECLARATION</p>
        <p class="rsn-p">I solemnly and sincerely declare that:</p>
        <ol class="inc-ol">
          <li>I am not an undischarged bankrupt.</li>
          <li>
            I have not been convicted whether within or outside Malaysia of any offence:
            <ol type="a">
              <li>in connection with the promotion, formation or management of a corporation;</li>
              <li>involving fraud or dishonesty punishable on conviction with imprisonment for three months or more; or</li>
              <li>under section 213, 217, 218, 228 or 539, within a period of five years preceding the date of this declaration.</li>
            </ol>
          </li>
          <li>
            I have not been imprisoned for any offence referred to in paragraph 2 within the period of five years
            immediately preceding the date of this declaration.
          </li>
          <li class="dim">
            * I am an undischarged bankrupt but have been granted leave by the court under section 198(3)(b)
            to act as a director of …………………… (name of corporation)
          </li>
          <li class="dim">
            * I have been granted leave by Court under section 198 to be a director / promoter …
            (* Strike out whichever is inapplicable)
          </li>
          <li>
            I hereby consent to act as director of
            <CddDocVarField field-key="company_name" />.
            I confirm that the facts and information stated in this document are true.
          </li>
        </ol>

        <div class="rsn-yours">
          <div>Sign by Director(s)/Promoter(s):</div>
          <div class="rsn-sign-line">______________________________</div>
          <div>Name: <CddDocVarField :field-key="`dir${id}_name`" /></div>
          <div>NRIC / Passport No.: <CddDocVarField :field-key="`dir${id}_id_no`" /></div>
          <div>Date: <CddDocVarField field-key="declaration_date" /></div>
        </div>

        <div class="rsn-attestation">
          <div class="rsn-sign-t">Before me:</div>
          <div class="rsn-sign-line">______________________________</div>
          <div>Commissioner of Oaths / Notary Public</div>
          <div class="rsn-date">
            Date of Declaration:
            <CddDocVarField field-key="declaration_date" />
          </div>
        </div>
      </PagedDoc>
    </div>
  </div>
</template>

<style scoped>
.rsn-sheets { color:#111; --a4-w:794px; --a4-h:1123px; --a4-margin:96px; }
.rsn-doc { width:var(--a4-w); max-width:100%; margin-left:auto; margin-right:auto; }
.rsn-doc + .rsn-doc { margin-top:34px; }
.rsn-doclabel { display:flex; align-items:center; gap:7px; padding:0 4px 9px; }
.rsn-doclabel .ic { font-size:14px; line-height:1; }
.rsn-doclabel .nm { font-size:12px; font-weight:800; color:#42506a; letter-spacing:.01em; }
.rsn-doclabel .sub { font-weight:700; color:#8a97b5; }
.rsn-doclabel .n { margin-left:auto; font-size:10.5px; font-weight:800; color:#9aa6bd; letter-spacing:.02em; white-space:nowrap; }

.rsn-center { text-align:center; margin:10px 0; }
.rsn-h { text-align:center; font-weight:800; font-size:15px; letter-spacing:.02em; margin:18px 0 14px; text-decoration:underline; }
.rsn-p { font-size:12.5px; line-height:1.7; margin:8px 0; }
.rsn-p.bold, .bold { font-weight:800; }

.rsn-list { margin:12px 0 6px; }
.rsn-list-h th { background:#f2f5fa; font-weight:800; font-size:11.5px; color:#42506a; }
.rsn-idx { text-align:center; font-weight:700; color:#5b6472; vertical-align:middle; }
.rsn-line { font-size:12.5px; line-height:1.8; }
.rsn-line.dim { color:#42506a; }
.rsn-rm { text-align:center; vertical-align:middle; }
.rsn-x { border:none; background:#fdecec; color:#d64545; width:22px; height:22px; border-radius:6px; font-size:11px; cursor:pointer; font-weight:700; }
.rsn-x:hover { background:#f9d5d5; }
.rsn-addrow td { padding:0 !important; border:1px solid #000; border-top:none; }
.rsn-add { width:100%; display:flex; align-items:center; justify-content:center; gap:6px; background:#eef4ff; border:none; color:#1B64DA; font-size:12.5px; font-weight:800; padding:9px; cursor:pointer; transition:background .15s; }
.rsn-add:hover { background:#dfeaff; }

.rsn-sign-t { font-weight:800; font-size:12.5px; margin-bottom:10px; }
.rsn-sign-line { letter-spacing:1px; color:#333; margin:18px 0 8px; }
.rsn-date { font-size:12.5px; font-weight:700; margin-top:14px; }
.rsn-yours { margin-top:28px; font-size:12.5px; line-height:2; }
.rsn-decl { margin-top:16px; font-size:12px; line-height:1.7; }
.rsn-attestation { margin-top:28px; font-size:12.5px; line-height:1.8; border-top:1px dashed #c5cddb; padding-top:16px; }

.rsn-act { font-weight:800; font-size:14px; }
.rsn-act.sm { font-size:11.5px; font-weight:700; margin-top:4px; line-height:1.5; }
.rsn-secbar { background:#e9edf4; font-weight:800; font-size:12px; padding:6px 10px; margin:16px 0 0; border:1px solid #000; }

.inc-part { margin-top:14px; }
.inc-part .rsn-part-h td {
  background:#5b6472;
  color:#fff;
  font-weight:800;
  font-size:12px;
  letter-spacing:.04em;
  text-align:center;
  padding:7px 10px;
  position:relative;
}
.inc-part .part-x {
  position:absolute;
  right:8px;
  top:50%;
  transform:translateY(-50%);
}
.inc-part .part-n {
  position:absolute;
  left:10px;
  top:50%;
  transform:translateY(-50%);
  font-size:10.5px;
  font-weight:700;
  opacity:.85;
}
.rsn-tall td { min-height:52px; height:52px; vertical-align:top; }
.rsn-addr td { min-height:72px; height:72px; }
.rsn-blank td { height:28px; background:#fff; padding:6px 8px !important; border-top:none; border-bottom:none; }
.inc-block-gap {
  height: 20px;
  width: 100%;
  display: block;
  flex-shrink: 0;
}
/* 페이지 분할 단위 — margin collapse 방지로 높이 측정 안정화 */
.pg-unit {
  display: flow-root;
  width: 100%;
}
.rsn-sheets .cdd-tbl { margin-top: 0; }
.rsn-sheets .rsn-secbar + .cdd-tbl { margin-top: -1px; }
.rsn-sheets .inc-block-gap + .pg-unit .cdd-tbl { margin-top: 0 !important; }
.rsn-add-ic { font-size: 0; }
.rsn-add-ic::before {
  content: '+';
  font-size: 16px;
  font-weight: 800;
  line-height: 1;
  font-family: Arial, Helvetica, sans-serif;
}
.inc-add {
  width:100%;
  margin-top:0;
  border:1px solid #000;
  border-top:none;
  border-radius:0;
}

.inc-ol { font-size:12.5px; line-height:1.7; margin:8px 0 8px 18px; padding:0; }
.inc-ol > li { margin:6px 0; }
.inc-ol ol { margin:4px 0 4px 18px; }
.inc-ol .dim { color:#6b7280; font-size:11.5px; }
</style>

<style>
.paper:has(.rsn-sheets) { background:transparent !important; border:none !important; box-shadow:none !important; padding:0 !important; }
</style>
